import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  Loader2,
  Plus,
  Save,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useBusiness } from '../../../contexts/BusinessContext';
import {
  buildFormConfig,
  buildLeadFormPublicUrl,
  DEFAULT_LEAD_FORM_CONFIG,
  fetchLeadFormById,
  getLeadFormPublicSlug,
  normalizeLeadFormSlug,
  updateLeadForm,
  type LeadForm,
  type LeadFormConfig,
  type LeadFormField,
} from '../../../lib/crm/leadForms';
import LeadFormAppearancePanel from '../../../components/crm/lead-forms/LeadFormAppearancePanel';
import LeadFormCanvas from '../../../components/crm/lead-forms/LeadFormCanvas';
import LeadFormColorPickerModal, {
  type LeadFormColorTarget,
} from '../../../components/crm/lead-forms/LeadFormColorPickerModal';
import LeadFormFieldLibrary from '../../../components/crm/lead-forms/LeadFormFieldLibrary';
import LeadFormFieldSettingsModal from '../../../components/crm/lead-forms/LeadFormFieldSettingsModal';
import LeadFormLivePreview from '../../../components/crm/lead-forms/LeadFormLivePreview';
import LeadFormSharePanel from '../../../components/crm/lead-forms/LeadFormSharePanel';

type BuilderTab = 'fields' | 'appearance' | 'share';

const shell = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
};

export default function LeadFormBuilderPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<LeadForm | null>(null);
  const [title, setTitle] = useState('');
  const [publicSlug, setPublicSlug] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [config, setConfig] = useState<LeadFormConfig>(DEFAULT_LEAD_FORM_CONFIG);
  const [fields, setFields] = useState<LeadFormField[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tab, setTab] = useState<BuilderTab>('fields');
  const [fieldPickerOpen, setFieldPickerOpen] = useState(false);
  const [settingsField, setSettingsField] = useState<LeadFormField | null>(null);
  const [colorPicker, setColorPicker] = useState<{
    target: LeadFormColorTarget;
    color: string;
  } | null>(null);

  const load = useCallback(async () => {
    if (!businessId || !formId) return;
    setLoading(true);
    try {
      const data = await fetchLeadFormById(businessId, formId);
      if (!data) {
        toast.error('Lead form not found');
        navigate('/crm/lead-forms', { replace: true });
        return;
      }
      setForm(data);
      setTitle(data.title || '');
      setPublicSlug(getLeadFormPublicSlug(data));
      setIsActive(data.is_active !== false);
      const cfg = { ...DEFAULT_LEAD_FORM_CONFIG, ...(data.form_config || {}) };
      setConfig(cfg);
      setFields(cfg.fields || []);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load lead form');
      navigate('/crm/lead-forms', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [businessId, formId, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const markDirty = () => setHasUnsavedChanges(true);

  const publicUrl = useMemo(() => {
    if (!selectedBusiness?.slug) return null;
    const slug = normalizeLeadFormSlug(publicSlug) || getLeadFormPublicSlug({ title, public_slug: publicSlug });
    if (!slug) return null;
    return buildLeadFormPublicUrl(window.location.origin, selectedBusiness.slug, slug);
  }, [selectedBusiness?.slug, publicSlug, title]);

  const logoUrl =
    selectedBusiness?.booking_settings?.logo_url || config.logo_url || null;

  const patchConfig = (patch: Partial<LeadFormConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
    markDirty();
  };

  const handleAddField = (field: LeadFormField) => {
    setFields((prev) => [...prev, field]);
    markDirty();
    setFieldPickerOpen(false);
  };

  const handleRemoveField = (fieldId: string) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    markDirty();
  };

  const handleMoveUp = (fieldId: string) => {
    setFields((prev) => {
      const i = prev.findIndex((f) => f.id === fieldId);
      if (i <= 0) return prev;
      const next = [...prev];
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    });
    markDirty();
  };

  const handleMoveDown = (fieldId: string) => {
    setFields((prev) => {
      const i = prev.findIndex((f) => f.id === fieldId);
      if (i < 0 || i >= prev.length - 1) return prev;
      const next = [...prev];
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return next;
    });
    markDirty();
  };

  const handleSave = async () => {
    if (!businessId || !form) return;
    if (!title.trim()) {
      toast.error('Form title is required');
      return;
    }
    const slug = normalizeLeadFormSlug(publicSlug) || normalizeLeadFormSlug(title);
    if (!slug) {
      toast.error('Custom URL is required');
      return;
    }
    setSaving(true);
    try {
      const form_config = buildFormConfig(fields, config);
      await updateLeadForm(businessId, form.id, {
        title: title.trim(),
        is_active: isActive,
        form_config,
        public_slug: slug,
      });
      setPublicSlug(slug);
      setConfig(form_config);
      setForm((prev) =>
        prev
          ? {
              ...prev,
              title: title.trim(),
              is_active: isActive,
              form_config,
              public_slug: slug,
            }
          : prev,
      );
      setHasUnsavedChanges(false);
      toast.success('Saved');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (hasUnsavedChanges) {
      toast.error('Save your changes before copying the link');
      return;
    }
    if (!publicUrl) {
      toast.error('Set a custom URL first');
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleOpenPreview = () => {
    if (hasUnsavedChanges) {
      toast.error('Save your changes before opening the live form');
      return;
    }
    if (!publicUrl) {
      toast.error('Set a custom URL first');
      return;
    }
    window.open(publicUrl, '_blank', 'noopener,noreferrer');
  };

  const handleColorSelect = (color: string) => {
    if (!colorPicker) return;
    const key =
      colorPicker.target === 'button'
        ? 'button_color'
        : (`${colorPicker.target}_color` as keyof LeadFormConfig);
    patchConfig({ [key]: color } as Partial<LeadFormConfig>);
    setColorPicker(null);
  };

  if (loading) {
    return (
      <div
        className="bg-[#f1f2f4] flex items-center justify-center"
        style={{
          ...shell,
          height: 'calc(100dvh - var(--control-centre-activity-rail-top, 3rem))',
        }}
      >
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!form) return null;

  const tabs: { id: BuilderTab; label: string }[] = [
    { id: 'fields', label: 'Fields' },
    { id: 'appearance', label: 'Appearance' },
    { id: 'share', label: 'Share' },
  ];

  const preview = (
    <LeadFormLivePreview
      title={title}
      config={config}
      fields={fields}
      business={{
        name: selectedBusiness?.name,
        phone: selectedBusiness?.phone,
        email: selectedBusiness?.email,
        address: selectedBusiness?.address,
        logoUrl,
      }}
    />
  );

  return (
    <div
      className="bg-[#f1f2f4] flex flex-col overflow-hidden"
      style={{
        ...shell,
        height: 'calc(100dvh - var(--control-centre-activity-rail-top, 3rem))',
      }}
      data-crm-lead-forms
    >
      {/* Header */}
      <div className="shrink-0 z-20 bg-white border-b border-gray-200/60">
        <div className="px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => navigate('/crm/lead-forms')}
            className="p-2 rounded-[10px] active:bg-gray-100 touch-manipulation flex-shrink-0"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Back to library"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>

          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                markDirty();
              }}
              className="w-full text-[15px] sm:text-[16px] font-semibold text-gray-900 bg-transparent border-0 focus:ring-0 p-0 truncate"
              style={{ letterSpacing: '-0.01em' }}
              placeholder="Form title"
            />
            {hasUnsavedChanges ? (
              <p className="text-[11px] text-amber-600 font-medium">Unsaved changes</p>
            ) : (
              <p className="text-[11px] text-gray-400">Lead form</p>
            )}
          </div>

          <button
            type="button"
            onClick={handleOpenPreview}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-gray-200 text-[13px] font-semibold text-gray-700 touch-manipulation"
            style={{ minHeight: 40, WebkitTapHighlightColor: 'transparent' }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Preview
          </button>
          <button
            type="button"
            onClick={() => void handleCopyLink()}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-gray-200 text-[13px] font-semibold text-gray-700 touch-manipulation"
            style={{ minHeight: 40, WebkitTapHighlightColor: 'transparent' }}
          >
            <Copy className="h-3.5 w-3.5" />
            Copy
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !hasUnsavedChanges}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold disabled:opacity-50 touch-manipulation"
            style={{ minHeight: 40, WebkitTapHighlightColor: 'transparent' }}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        {/* Preview — desktop only */}
        <div className="hidden lg:flex flex-1 items-start justify-center p-4 sm:p-6 overflow-y-auto bg-[#e8eaed]/60">
          {preview}
        </div>

        {/* Editor panel */}
        <div className="flex w-full lg:w-[420px] xl:w-[460px] flex-col bg-white border-t lg:border-t-0 lg:border-l border-gray-200/60 min-h-0">
          <div className="flex border-b border-gray-200/60 px-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex-1 py-3 text-[13px] font-semibold border-b-2 transition-colors touch-manipulation ${
                  tab === t.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent', minHeight: 44 }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {tab === 'fields' ? (
              <div className="space-y-5">
                <div className="hidden lg:block pb-4 border-b border-gray-100">
                  <LeadFormFieldLibrary onAddField={handleAddField} />
                </div>

                <div className="lg:hidden">
                  <button
                    type="button"
                    onClick={() => setFieldPickerOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 mb-4 rounded-[10px] bg-blue-50 text-blue-600 text-[14px] font-semibold touch-manipulation"
                    style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-4 w-4" />
                    Add fields
                  </button>
                </div>

                <div>
                  <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-[0.04em] mb-2">
                    Form fields
                  </p>
                  <LeadFormCanvas
                    fields={fields}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    onOpenSettings={setSettingsField}
                    onRemove={handleRemoveField}
                    onRequestAddFields={() => setFieldPickerOpen(true)}
                  />
                </div>
              </div>
            ) : null}

            {tab === 'appearance' ? (
              <LeadFormAppearancePanel
                config={config}
                onChange={patchConfig}
                onPickColor={(target, color) => setColorPicker({ target, color })}
              />
            ) : null}

            {tab === 'share' ? (
              <LeadFormSharePanel
                publicUrl={publicUrl}
                businessSlug={selectedBusiness?.slug || null}
                publicSlug={publicSlug}
                config={config}
                isActive={isActive}
                onChangeConfig={patchConfig}
                onChangeActive={(v) => {
                  setIsActive(v);
                  markDirty();
                }}
                onChangePublicSlug={(slug) => {
                  setPublicSlug(slug);
                  markDirty();
                }}
                onCopyLink={() => void handleCopyLink()}
                onOpenPreview={handleOpenPreview}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Mobile field picker sheet */}
      {fieldPickerOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[999999] flex items-end bg-black/50 backdrop-blur-sm lg:hidden"
              onClick={() => setFieldPickerOpen(false)}
            >
              <div
                className="w-full bg-white rounded-t-[16px] max-h-[80vh] overflow-y-auto p-4 pb-8"
                onClick={(e) => e.stopPropagation()}
                style={shell}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[16px] font-semibold text-gray-900">Add field</h3>
                  <button
                    type="button"
                    onClick={() => setFieldPickerOpen(false)}
                    className="p-2 rounded-[10px] active:bg-gray-100"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                <LeadFormFieldLibrary onAddField={handleAddField} compact />
              </div>
            </div>,
            document.body,
          )
        : null}

      {settingsField ? (
        <LeadFormFieldSettingsModal
          field={settingsField}
          onClose={() => setSettingsField(null)}
          onSave={(updated) => {
            setFields((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
            setSettingsField(null);
            markDirty();
          }}
        />
      ) : null}

      {colorPicker ? (
        <LeadFormColorPickerModal
          target={colorPicker.target}
          currentColor={colorPicker.color}
          onClose={() => setColorPicker(null)}
          onSelect={handleColorSelect}
        />
      ) : null}
    </div>
  );
}
